import { useTranslation } from "@redotech/redo-i18n";
import { CompensationMethodMatchType } from "@redotech/redo-return-core-common/return-flow/condition-enums";
import type { CompensationMethodState } from "@redotech/redo-return-core-common/return-flow/condition-validate";
import { ReturnOptionMethod } from "@redotech/redo-return-core-common/return-flow/return-option";
import { ConditionTypeDetailsProps } from "@redotech/redo-return-ui/flows/return-flow/condition";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import {
  RadioGroupOption,
  RedoRadioGroup,
} from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { memo, useMemo } from "react";

function matchTypeOptions(
  t: (key: string) => string,
): RadioGroupOption<CompensationMethodMatchType>[] {
  return [
    { id: CompensationMethodMatchType.CONTAINS, label: t("Contains") },
    { id: CompensationMethodMatchType.ALL, label: t("Has all") },
  ];
}

function compensationMethodLabel(
  method: ReturnOptionMethod["type"],
  t: (key: string) => string,
): string {
  switch (method) {
    case ReturnOptionMethod.EXCHANGE:
      return t("Exchange");
    case ReturnOptionMethod.REFUND:
      return t("Refund");
    case ReturnOptionMethod.STORE_CREDIT:
      return t("Store credit");
    case ReturnOptionMethod.REPAIR:
      return t("Repair");
  }
}

const COMPENSATION_METHOD_OPTIONS: readonly ReturnOptionMethod["type"][] = [
  ReturnOptionMethod.EXCHANGE,
  ReturnOptionMethod.REFUND,
  ReturnOptionMethod.STORE_CREDIT,
  ReturnOptionMethod.REPAIR,
];

export const CompensationMethodDetails = memo(
  function CompensationMethodDetails({
    state,
    setState,
    disabled,
  }: ConditionTypeDetailsProps<CompensationMethodState>) {
    const { t } = useTranslation();
    const compensationMethodOptions: BadgeRedoListItem<
      ReturnOptionMethod["type"]
    >[] = useMemo(
      () =>
        COMPENSATION_METHOD_OPTIONS.map((method) => ({
          id: method,
          value: method,
          type: "badge" as const,
          badge: {
            text: compensationMethodLabel(method, t),
            color: "gray" as const,
          },
        })),
      [t],
    );

    const selectedOptions = useMemo(
      () =>
        compensationMethodOptions.filter((option) =>
          state.compensationMethods.includes(option.value),
        ),
      [compensationMethodOptions, state.compensationMethods],
    );

    return (
      <>
        <RedoRadioGroup
          disabled={disabled}
          options={matchTypeOptions(t)}
          setValue={(matchType) => setState({ ...state, matchType })}
          size="xs"
          value={state.matchType}
        />
        <RedoTagsDropdownInput
          description={t(
            "Contains matches if the return has any selected method; Has all matches only if the return has every selected method.",
          )}
          disabled={disabled}
          label={t("Compensation methods")}
          options={compensationMethodOptions}
          placeholder={t("Select methods...")}
          selectedOptions={selectedOptions}
          setSelectedOptions={(items) =>
            setState({
              ...state,
              compensationMethods: items.map((item) => item.value),
            })
          }
          size="xs"
        />
      </>
    );
  },
);
